// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import AppLayout from './layout/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { PermissionProvider } from './context/PermissionContext';
import { LanguageProvider } from './context/LanguageContext';
import { OrderNotificationProvider } from './context/OrderNotificationContext';

import Login from './pages/Login';
import ProductionLog from './pages/ProductionLog';
import AddLog from './pages/AddLog';
import TraceabilityPage from './pages/TraceabilityPage';
import InventoryPage from './pages/InventoryPage';
import NotFoundPage from './pages/NotFoundPage';

import AdminUserManagement from './components/AdminUserManagement';
import AdminProductPage from './pages/AdminProductPage';
import AdminOrdersPage from './pages/AdminOrdersPage';
import FactoryOrdersPage from './pages/FactoryOrdersPage';
import AdminCustomerPage from './pages/AdminCustomerPage';
import TrendsAnalyticsPage from './pages/TrendsAnalyticsPage';
import NewComplaintPage from './pages/trends/NewComplaintPage';
import ProductionLaborPage from './pages/trends/ProductionLaborPage';
import EnvironmentalConfigPage from './pages/trends/EnvironmentalConfigPage';
import EnvironmentalNewATPPage from './pages/trends/EnvironmentalNewATPPage';
import authService from './services/authService';
import BakingCcpConfigPage from './pages/ccp/BakingCcpConfigPage';
import BakingCcpStartPage from './pages/ccp/BakingCcpStartPage';
import BakingCcpLivePage from './pages/ccp/BakingCcpLivePage';
import BakingCcpQueuePage from './pages/ccp/BakingCcpQueuePage';
import PrepCcpConfigPage from './pages/ccp/PrepCcpConfigPage';
import PrepCcpStartPage from './pages/ccp/PrepCcpStartPage';
import PrepCcpLivePage from './pages/ccp/PrepCcpLivePage';
import PrepCcpVerifyPage from './pages/ccp/PrepCcpVerifyPage';
import BakingCcpVerifyPage from './pages/ccp/BakingCcpVerifyPage';
import ProductionSchedulePage from './pages/ProductionSchedulePage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import QboSyncPage from './pages/QboSyncPage';
import XeroSyncPage from './pages/XeroSyncPage';
import SuperAdminPage from './pages/SuperAdminPage';
import IngredientInventoryPage from './pages/IngredientInventoryPage';
import RecipeLibraryPage from './pages/RecipeLibraryPage';
import PercentageRecipeEditorPage from './pages/PercentageRecipeEditorPage';
import PercentageRecipeRunPage from './pages/PercentageRecipeRunPage';
import DoughRecipeEditorPage from './pages/DoughRecipeEditorPage';
import KnowledgeBaseHubPage from './pages/KnowledgeBaseHubPage';
import DoughRecipeRunPage from './pages/DoughRecipeRunPage';
import PackagingInventoryPage from './pages/PackagingInventoryPage';
import QuotesListPage from './pages/QuotesListPage';
import QuoteBuilderPage from './pages/QuoteBuilderPage';
import BillOfLadingPage from './pages/BillOfLadingPage';
import BolPrintPage from './pages/BolPrintPage';
import SupplierFsvpPacketPage from './pages/SupplierFsvpPacketPage';
import PalletLabelPage from './pages/PalletLabelPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DocumentLibraryPage from './pages/DocumentLibraryPage';
import ProductLabelsPage from './pages/ProductLabelsPage';
import LabelDesignsListPage from './pages/LabelDesignsListPage';
import LabelDesignPage from './pages/LabelDesignPage';
import LabelDetailPage from './pages/LabelDetailPage';
import TracePage from './pages/TracePage';
import SqfDashboardPage from './pages/sqf/SqfDashboardPage';
import SqfChecklistListPage from './pages/sqf/SqfChecklistListPage';
import SqfFillPage from './pages/sqf/SqfFillPage';
import SqfLogDetailPage from './pages/sqf/SqfLogDetailPage';
import SqfTemplateListPage from './pages/sqf/SqfTemplateListPage';
import SqfTemplateBuilderPage from './pages/sqf/SqfTemplateBuilderPage';
import SqfSchedulePage from './pages/sqf/SqfSchedulePage';
import SqfScheduleOverviewPage from './pages/sqf/SqfScheduleOverviewPage';
import SqfCorrectiveActionsPage from './pages/sqf/SqfCorrectiveActionsPage';
import SqfContractorsPage from './pages/sqf/SqfContractorsPage';
import SqfVisitorLogPage from './pages/sqf/SqfVisitorLogPage';
import SqfPoliciesPage from './pages/sqf/SqfPoliciesPage';
import TempMonitoringPage from './pages/sqf/TempMonitoringPage';
import TempMonitoringConfigPage from './pages/sqf/TempMonitoringConfigPage';
import SqfPolicyEditorPage from './pages/sqf/SqfPolicyEditorPage';
import SqfTemplateAuditPage from './pages/sqf/SqfTemplateAuditPage';
import SqfAiImportPage from './pages/sqf/SqfAiImportPage';
import SqfAuditHubPage from './pages/sqf/SqfAuditHubPage';
import SqfTrainingLibraryPage from './pages/sqf/SqfTrainingLibraryPage';
import SqfTrainingBuilderPage from './pages/sqf/SqfTrainingBuilderPage';
import SqfTrainingAssignmentsPage from './pages/sqf/SqfTrainingAssignmentsPage';
import FoodSafetyPlanIndexPage from './pages/sqf/FoodSafetyPlanIndexPage';
import FoodSafetyPlanEditorPage from './pages/sqf/FoodSafetyPlanEditorPage';
import FoodSafetyLibraryPage from './pages/sqf/FoodSafetyLibraryPage';
import FoodSafetyPrintPage from './pages/sqf/FoodSafetyPrintPage';
import FoodSafetyWizardPage from './pages/sqf/FoodSafetyWizardPage';
import FoodSafetyPublicPlanPage from './pages/sqf/FoodSafetyPublicPlanPage';
import CustomerOrderPortalPage from './pages/CustomerOrderPortalPage';
import { SqfImportProvider } from './context/SqfImportContext';
import TimeClock from './pages/TimeClock';
import { PortalAuthProvider, usePortalAuth } from './context/PortalAuthContext';
import PortalLoginPage from './pages/portal/PortalLoginPage';
import PortalChangePasswordPage from './pages/portal/PortalChangePasswordPage';
import PortalLayout from './pages/portal/PortalLayout';
import PortalDashboardPage from './pages/portal/PortalDashboardPage';
import PortalHoursPage from './pages/portal/PortalHoursPage';
import PortalPayPage from './pages/portal/PortalPayPage';
import PortalPtoPage from './pages/portal/PortalPtoPage';
import PortalPointsPage from './pages/portal/PortalPointsPage';
import PortalProfilePage from './pages/portal/PortalProfilePage';
import PortalTrainingPage from './pages/portal/PortalTrainingPage';
import PortalTrainingQuizPage from './pages/portal/PortalTrainingQuizPage';
import TimeTrackingEmployeesPage from './pages/TimeTrackingEmployeesPage';
import TimeTrackingTimecardsPage from './pages/TimeTrackingTimecardsPage';
import TimeTrackingReportsPage from './pages/TimeTrackingReportsPage';
import TimeOffDashboardPage from './pages/TimeOffDashboardPage';
import TimeOffPoliciesPage from './pages/TimeOffPoliciesPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import GS1ImportPage from './pages/GS1ImportPage';
import YearEndBonusPage from './pages/YearEndBonusPage';
import PtoRequestsPage from './pages/PtoRequestsPage';
import { usePermissions } from './context/PermissionContext';
import { registerServiceWorker } from './services/pushService';
import EventsPage from './pages/events/EventsPage';
import EventDetailPage from './pages/events/EventDetailPage';
import TradeShowsPage from './pages/tradeshows/TradeShowsPage';
import TradeShowDetailPage from './pages/tradeshows/TradeShowDetailPage';
import TradeShowAnalyticsPage from './pages/tradeshows/TradeShowAnalyticsPage';
import TradeShowCapturePage from './pages/tradeshows/TradeShowCapturePage';
import DriversPage from './pages/deliveryfleet/DriversPage';
import DriverDetailPage from './pages/deliveryfleet/DriverDetailPage';
import DispatchBoardPage from './pages/deliveryfleet/DispatchBoardPage';
import DeliveryTrackingPage from './pages/DeliveryTrackingPage';
import PortalDriverAgreementPage from './pages/portal/PortalDriverAgreementPage';
import PortalDriverOffersPage from './pages/portal/PortalDriverOffersPage';
import PortalDriverDeliveriesPage from './pages/portal/PortalDriverDeliveriesPage';
import PortalEventInventoryPage from './pages/portal/PortalEventInventoryPage';
import PortalEventsListPage from './pages/portal/PortalEventsListPage';
import PortalEventSchedulePage from './pages/portal/PortalEventSchedulePage';
import PortalDeliveriesPage from './pages/portal/PortalDeliveriesPage';
import PortalPodCapturePage from './pages/portal/PortalPodCapturePage';
import { MessagingProvider } from './context/MessagingContext';
import TechSheetPreviewPage from './pages/TechSheetPreviewPage';
import MaintenanceAssetsPage from './pages/maintenance/MaintenanceAssetsPage';
import MaintenanceAssetDetailPage from './pages/maintenance/MaintenanceAssetDetailPage';
import MaintenanceScanPage from './pages/maintenance/MaintenanceScanPage';
import WorkOrderBoardPage from './pages/maintenance/WorkOrderBoardPage';
import WorkOrderDetailPage from './pages/maintenance/WorkOrderDetailPage';
import PmSchedulePage from './pages/maintenance/PmSchedulePage';
import MaintenancePartsPage from './pages/maintenance/MaintenancePartsPage';
import MaintenanceReportsPage from './pages/maintenance/MaintenanceReportsPage';
import MaintenanceVendorsPage from './pages/maintenance/MaintenanceVendorsPage';

// /portal home: event-only staff and drivers have no dashboard (pay/hours/etc.), so send them to
// their own confined slice.
function PortalIndex() {
  const { employee } = usePortalAuth();
  if (employee?.staffType === 'event_staff') return <Navigate to="/portal/events" replace />;
  if (employee?.staffType === 'driver') return <Navigate to="/portal/driver/offers" replace />;
  return <PortalDashboardPage />;
}

// Priority order of candidate landing pages — the first one this user actually has access to
// wins. A single hardcoded destination (the old behavior) lands any tenant/role with a smaller
// module set (a reseller tenant, the confined event_manager role, etc.) on an "Access Restricted"
// page for a module they were never given.
const HOME_CANDIDATES = [
  ['production.traceability', '/traceability'],
  ['events', '/events'],
  ['admin.orders', '/admin/orders'],
  ['production.logs', '/logs'],
  ['production.orders', '/factory/orders'],
  ['admin.customers', '/admin/customers'],
  ['admin.settings', '/admin/settings'],
];

function RootRedirect() {
  const { role, hasModule, loading } = usePermissions();
  if (loading) return null;
  if (role === 'superadmin') return <Navigate to="/superadmin" replace />;
  const home = HOME_CANDIDATES.find(([moduleKey]) => hasModule(moduleKey))?.[1] || '/admin/settings';
  return <Navigate to={home} replace />;
}

function App() {
  const isAuthed = authService.isTokenValid();
  if (!isAuthed) authService.clearToken();

  // Register the service worker on load so push notifications work
  useEffect(() => {
    if (isAuthed) registerServiceWorker();
  }, [isAuthed]);

  return (
    <Router>
      <LanguageProvider>
      <PermissionProvider>
        <OrderNotificationProvider>
        <MessagingProvider>
        <SqfImportProvider>
        <Routes>
          <Route element={<AppLayout />}>
          {/* Public */}
          <Route
            path="/"
            element={isAuthed ? <RootRedirect /> : <Login />}
          />
          {/* Login navigates here right after auth instead of a hardcoded path — `isAuthed`
              above is computed once at mount and won't have flipped yet immediately post-login,
              so routing through "/" would bounce back to <Login />. This route doesn't have that
              problem since ProtectedRoute/RootRedirect check the token fresh on every render. */}
          <Route path="/redirecting" element={<RootRedirect />} />

          {/* Production */}
          <Route path="/logs" element={
            <ProtectedRoute moduleKey="production.logs">
              <ProductionLog />
            </ProtectedRoute>
          } />
          <Route path="/add-log" element={
            <ProtectedRoute moduleKey="production.logs">
              <AddLog />
            </ProtectedRoute>
          } />
          <Route path="/traceability" element={
            <ProtectedRoute moduleKey="production.traceability">
              <TraceabilityPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute moduleKey="production.inventory">
              <InventoryPage />
            </ProtectedRoute>
          } />
          <Route path="/factory/orders" element={
            <ProtectedRoute moduleKey="production.orders">
              <FactoryOrdersPage />
            </ProtectedRoute>
          } />
          <Route path="/production-schedule" element={
            <ProtectedRoute moduleKey="production.schedule">
              <ProductionSchedulePage />
            </ProtectedRoute>
          } />
          <Route path="/ingredient-inventory" element={
            <ProtectedRoute moduleKey="ingredient.inventory">
              <IngredientInventoryPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes" element={
            <ProtectedRoute moduleKey="recipes.library">
              <RecipeLibraryPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/percentage/new" element={
            <ProtectedRoute moduleKey="recipes.manage">
              <PercentageRecipeEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/percentage/:id/edit" element={
            <ProtectedRoute moduleKey="recipes.manage">
              <PercentageRecipeEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/percentage/:id/run" element={
            <ProtectedRoute moduleKey="recipes.library">
              <PercentageRecipeRunPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/dough/new" element={
            <ProtectedRoute moduleKey="recipes.manage">
              <DoughRecipeEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/dough/:id/edit" element={
            <ProtectedRoute moduleKey="recipes.manage">
              <DoughRecipeEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/dough/:id/run" element={
            <ProtectedRoute moduleKey="recipes.library">
              <DoughRecipeRunPage />
            </ProtectedRoute>
          } />
          <Route path="/recipes/knowledge-base" element={
            <ProtectedRoute moduleKey="recipes.knowledge_base">
              <KnowledgeBaseHubPage />
            </ProtectedRoute>
          } />
          <Route path="/quotes" element={
            <ProtectedRoute moduleKey="sales.quotes">
              <QuotesListPage />
            </ProtectedRoute>
          } />
          <Route path="/quotes/new" element={
            <ProtectedRoute moduleKey="sales.quotes">
              <QuoteBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="/quotes/:id" element={
            <ProtectedRoute moduleKey="sales.quotes">
              <QuoteBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="/packaging" element={
            <ProtectedRoute moduleKey="packaging.inventory">
              <PackagingInventoryPage />
            </ProtectedRoute>
          } />

          {/* Maintenance */}
          <Route path="/maintenance/assets" element={
            <ProtectedRoute moduleKey="maintenance.assets">
              <MaintenanceAssetsPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/assets/:id" element={
            <ProtectedRoute moduleKey="maintenance.assets">
              <MaintenanceAssetDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/scan" element={
            <ProtectedRoute moduleKey="maintenance.assets">
              <MaintenanceScanPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/scan/:qrCodeKey" element={
            <ProtectedRoute moduleKey="maintenance.assets">
              <MaintenanceScanPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/work-orders" element={
            <ProtectedRoute moduleKey="maintenance.workorders">
              <WorkOrderBoardPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/work-orders/:id" element={
            <ProtectedRoute moduleKey="maintenance.workorders">
              <WorkOrderDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/pm-schedule" element={
            <ProtectedRoute moduleKey="maintenance.pm">
              <PmSchedulePage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/parts" element={
            <ProtectedRoute moduleKey="maintenance.parts">
              <MaintenancePartsPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/reports" element={
            <ProtectedRoute moduleKey="maintenance.reports">
              <MaintenanceReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/maintenance/vendors" element={
            <ProtectedRoute moduleKey="maintenance.workorders">
              <MaintenanceVendorsPage />
            </ProtectedRoute>
          } />
          <Route path="/bol/:id/pallet-labels" element={
            <ProtectedRoute moduleKey="shipping.bol.pallet_labels">
              <PalletLabelPage />
            </ProtectedRoute>
          } />
          <Route path="/shipping/bol" element={
            <ProtectedRoute moduleKey="shipping.bol">
              <BillOfLadingPage />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute moduleKey="documents.library">
              <DocumentLibraryPage />
            </ProtectedRoute>
          } />
          <Route path="/labels" element={
            <ProtectedRoute moduleKey="labels.view">
              <ProductLabelsPage />
            </ProtectedRoute>
          } />
          <Route path="/labels/:id" element={
            <ProtectedRoute moduleKey="labels.view">
              <LabelDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/label-designs" element={
            <ProtectedRoute moduleKey="labels.design">
              <LabelDesignsListPage />
            </ProtectedRoute>
          } />
          <Route path="/label-designs/new" element={
            <ProtectedRoute moduleKey="labels.design">
              <LabelDesignPage />
            </ProtectedRoute>
          } />
          <Route path="/label-designs/:id/edit" element={
            <ProtectedRoute moduleKey="labels.design">
              <LabelDesignPage />
            </ProtectedRoute>
          } />

          {/* Super Admin */}
          <Route path="/superadmin" element={
            <ProtectedRoute requiredRole="superadmin">
              <SuperAdminPage />
            </ProtectedRoute>
          } />

          {/* Admin */}
          <Route path="/admin/settings" element={
            <ProtectedRoute moduleKey="admin.settings">
              <CompanySettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/qbo/sync" element={
            <ProtectedRoute moduleKey="admin.settings">
              <QboSyncPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/xero/sync" element={
            <ProtectedRoute moduleKey="admin.settings">
              <XeroSyncPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/products" element={
            <ProtectedRoute moduleKey="admin.products">
              <AdminProductPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/gs1-import" element={
            <ProtectedRoute moduleKey="admin.products">
              <GS1ImportPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute moduleKey="admin.users">
              <AdminUserManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/customers" element={
            <ProtectedRoute moduleKey="admin.customers">
              <AdminCustomerPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute moduleKey="admin.orders">
              <AdminOrdersPage />
            </ProtectedRoute>
          } />

          {/* Analytics */}
          <Route path="/trends/*" element={
            <ProtectedRoute moduleKey="analytics.overview">
              <TrendsAnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/trends/production/labor" element={
            <ProtectedRoute moduleKey="analytics.labor">
              <ProductionLaborPage />
            </ProtectedRoute>
          } />
          <Route path="/trends/complaints/new" element={
            <ProtectedRoute moduleKey="analytics.complaints">
              <NewComplaintPage />
            </ProtectedRoute>
          } />
          <Route path="/trends/environmental/config" element={
            <ProtectedRoute moduleKey="analytics.environmental">
              <EnvironmentalConfigPage />
            </ProtectedRoute>
          } />
          <Route path="/trends/environmental/new" element={
            <ProtectedRoute moduleKey="analytics.environmental">
              <EnvironmentalNewATPPage />
            </ProtectedRoute>
          } />

          {/* CCP — Production */}
          <Route path="/ccp/baking/config" element={
            <ProtectedRoute moduleKey="ccp.baking.config">
              <BakingCcpConfigPage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/baking/start" element={
            <ProtectedRoute moduleKey="ccp.baking.production">
              <BakingCcpStartPage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/baking/live/:runId" element={
            <ProtectedRoute moduleKey="ccp.baking.production">
              <BakingCcpLivePage />
            </ProtectedRoute>
          } />

          {/* CCP — QA */}
          <Route path="/ccp/baking/queue" element={
            <ProtectedRoute moduleKey="ccp.baking.qa">
              <BakingCcpQueuePage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/baking/verify/:runId" element={
            <ProtectedRoute moduleKey="ccp.baking.qa">
              <BakingCcpVerifyPage />
            </ProtectedRoute>
          } />

          {/* Prep CCP — cook+cool for intermediate products, independent of Baking CCP */}
          <Route path="/ccp/prep/config" element={
            <ProtectedRoute moduleKey="ccp.prep.config">
              <PrepCcpConfigPage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/prep/start" element={
            <ProtectedRoute moduleKey="ccp.prep.production">
              <PrepCcpStartPage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/prep/live/:runId" element={
            <ProtectedRoute moduleKey="ccp.prep.production">
              <PrepCcpLivePage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/prep/verify" element={
            <ProtectedRoute moduleKey="ccp.prep.qa">
              <PrepCcpVerifyPage />
            </ProtectedRoute>
          } />
          <Route path="/ccp/prep/verify/:runId" element={
            <ProtectedRoute moduleKey="ccp.prep.qa">
              <PrepCcpVerifyPage />
            </ProtectedRoute>
          } />

          {/* SQF Daily Checklists */}
          <Route path="/sqf" element={
            <ProtectedRoute moduleKey="sqf.checklists.view">
              <SqfDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/checklists" element={
            <ProtectedRoute moduleKey="sqf.checklists.view">
              <SqfChecklistListPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/fill/:instanceId" element={
            <ProtectedRoute moduleKey="sqf.checklists.fill">
              <SqfFillPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/logs/:logId" element={
            <ProtectedRoute moduleKey="sqf.checklists.view">
              <SqfLogDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/templates" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfTemplateListPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/templates/:id/edit" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfTemplateBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/schedules" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfScheduleOverviewPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/templates/:id/schedule" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfSchedulePage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/templates/:id/audit" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfTemplateAuditPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/corrective-actions" element={
            <ProtectedRoute moduleKey="sqf.checklists.corrective">
              <SqfCorrectiveActionsPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/contractors" element={
            <ProtectedRoute anyOf={['sqf.checklists.manage', 'sqf.entrance']}>
              <SqfContractorsPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/visitors" element={
            <ProtectedRoute anyOf={['sqf.checklists.manage', 'sqf.entrance']}>
              <SqfVisitorLogPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/policies" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfPoliciesPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/policies/:id/edit" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfPolicyEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/temp-monitoring" element={
            <ProtectedRoute moduleKey="sqf.temp_monitoring">
              <TempMonitoringPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/temp-monitoring/config" element={
            <ProtectedRoute moduleKey="sqf.temp_monitoring">
              <TempMonitoringConfigPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/ai-import" element={
            <ProtectedRoute moduleKey="sqf.checklists.view">
              <SqfAiImportPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/hub" element={
            <ProtectedRoute moduleKey="sqf.checklists.manage">
              <SqfAuditHubPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/training" element={
            <ProtectedRoute moduleKey="sqf.training.manage">
              <SqfTrainingLibraryPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/training/:id/edit" element={
            <ProtectedRoute moduleKey="sqf.training.manage">
              <SqfTrainingBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/training/assignments" element={
            <ProtectedRoute moduleKey="sqf.training.manage">
              <SqfTrainingAssignmentsPage />
            </ProtectedRoute>
          } />

          {/* Food Safety Plans (HACCP) */}
          <Route path="/sqf/food-safety" element={
            <ProtectedRoute moduleKey="sqf.food_safety">
              <FoodSafetyPlanIndexPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/food-safety/library" element={
            <ProtectedRoute moduleKey="sqf.food_safety">
              <FoodSafetyLibraryPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/food-safety/wizard" element={
            <ProtectedRoute moduleKey="sqf.food_safety">
              <FoodSafetyWizardPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/food-safety/:planType/print" element={
            <ProtectedRoute moduleKey="sqf.food_safety">
              <FoodSafetyPrintPage />
            </ProtectedRoute>
          } />
          <Route path="/sqf/food-safety/:planType" element={
            <ProtectedRoute moduleKey="sqf.food_safety">
              <FoodSafetyPlanEditorPage />
            </ProtectedRoute>
          } />

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Time Tracking — admin pages */}
          <Route path="/admin/time-tracking" element={
            <ProtectedRoute moduleKey="time.tracking.manage">
              <TimeTrackingEmployeesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/time-tracking/timecards" element={
            <ProtectedRoute moduleKey="time.tracking.view">
              <TimeTrackingTimecardsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/time-tracking/reports" element={
            <ProtectedRoute moduleKey="time.tracking.reports">
              <TimeTrackingReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking/time-off" element={
            <ProtectedRoute moduleKey="time.tracking.time_off">
              <TimeOffDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking/time-off/policies" element={
            <ProtectedRoute moduleKey="time.tracking.time_off">
              <TimeOffPoliciesPage />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking/pto-requests" element={
            <ProtectedRoute moduleKey="time.tracking.time_off">
              <PtoRequestsPage />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking/year-end-bonus" element={
            <ProtectedRoute moduleKey="time.tracking.manage">
              <YearEndBonusPage />
            </ProtectedRoute>
          } />

          {/* Notification preferences */}
          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationPreferencesPage />
            </ProtectedRoute>
          } />

          {/* Employee Portal — separate auth, no main-app auth required */}
          <Route path="/events" element={
            <ProtectedRoute moduleKey="events">
              <EventsPage />
            </ProtectedRoute>
          } />
          <Route path="/events/:id" element={
            <ProtectedRoute moduleKey="events">
              <EventDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-shows" element={
            <ProtectedRoute moduleKey="tradeshows">
              <TradeShowsPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-shows/analytics" element={
            <ProtectedRoute moduleKey="tradeshows">
              <TradeShowAnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/trade-shows/:id" element={
            <ProtectedRoute moduleKey="tradeshows">
              <TradeShowDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/delivery-fleet" element={
            <ProtectedRoute moduleKey="deliveryfleet.dispatch">
              <DispatchBoardPage />
            </ProtectedRoute>
          } />
          <Route path="/delivery-fleet/drivers" element={
            <ProtectedRoute moduleKey="deliveryfleet.drivers">
              <DriversPage />
            </ProtectedRoute>
          } />
          <Route path="/delivery-fleet/drivers/:id" element={
            <ProtectedRoute moduleKey="deliveryfleet.drivers">
              <DriverDetailPage />
            </ProtectedRoute>
          } />

          <Route path="/portal/login" element={<PortalAuthProvider><PortalLoginPage /></PortalAuthProvider>} />
          <Route path="/portal/change-password" element={<PortalAuthProvider><PortalChangePasswordPage /></PortalAuthProvider>} />
          <Route path="/portal" element={<PortalAuthProvider><PortalLayout /></PortalAuthProvider>}>
            <Route index element={<PortalIndex />} />
            <Route path="hours" element={<PortalHoursPage />} />
            <Route path="pay" element={<PortalPayPage />} />
            <Route path="pto" element={<PortalPtoPage />} />
            <Route path="points" element={<PortalPointsPage />} />
            <Route path="training" element={<PortalTrainingPage />} />
            <Route path="training/:id" element={<PortalTrainingQuizPage />} />
            <Route path="profile" element={<PortalProfilePage />} />
            <Route path="events" element={<PortalEventsListPage />} />
            <Route path="events/:eventId" element={<PortalEventInventoryPage />} />
            <Route path="events/:eventId/schedule" element={<PortalEventSchedulePage />} />
            <Route path="deliveries" element={<PortalDeliveriesPage />} />
            <Route path="deliveries/:orderId/pod" element={<PortalPodCapturePage />} />
            <Route path="driver/agreement" element={<PortalDriverAgreementPage />} />
            <Route path="driver/offers" element={<PortalDriverOffersPage />} />
            <Route path="driver/deliveries" element={<PortalDriverDeliveriesPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Chrome-less public/customer-facing routes — no sidebar/topbar ever */}
          <Route path="/share/food-safety/:token" element={<FoodSafetyPublicPlanPage />} />
          <Route path="/trace/:lotCode/:productId" element={<TracePage />} />
          <Route path="/trace/:lotCode" element={<TracePage />} />
          <Route path="/order/:token" element={<CustomerOrderPortalPage />} />
          <Route path="/tradeshow/:token" element={<TradeShowCapturePage />} />
          <Route path="/track/:token" element={<DeliveryTrackingPage />} />
          <Route path="/time-clock" element={<TimeClock />} />
          <Route path="/bol/print" element={<BolPrintPage />} />
          <Route path="/suppliers/fsvp-packet" element={<SupplierFsvpPacketPage />} />
          <Route path="/techsheets/:id/preview" element={<TechSheetPreviewPage />} />
        </Routes>
        </SqfImportProvider>
        </MessagingProvider>
        </OrderNotificationProvider>
      </PermissionProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;